// src/features/statistics/components/GeneralStatsToolbar.tsx

import { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  FileDown,
  Loader2,
  RefreshCw,
  TableProperties,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Skeleton } from '../../../components/ui/skeleton';

import { copyAccountStatsTsv, exportAccountStatsPdf } from '../lib/export-account-stats';
import { SHEETS_HINT_DURATION_MS, SHEETS_OPEN_DELAY_MS, SHEETS_URL } from '../lib/export-sheets';
import { isExactDemographicsMatch } from '../hooks/useDemographicsFilter';
import { DateRangeSelector } from './DateRangeSelector';
import { DemographicsFilterPanel } from './DemographicsFilterPanel';
import { SheetsClipboardHint } from './SheetsClipboardHint';

import type { AccountStatsRow, DateRangeOption } from '../types/account-stats';
import type { DemographicsFilter } from '../types/demographics-filter';
import type { RefObject } from 'react';

type GeneralStatsToolbarProps = {
  sortedRows: AccountStatsRow[];
  tableRef: RefObject<HTMLDivElement | null>;
  dateRangeOption: DateRangeOption;
  onDateRangeChange: (option: DateRangeOption) => void;
  comparePrev: boolean;
  onComparePrevToggle: () => void;
  isFetching: boolean;
  onRefresh: () => void;
  isLoadingTags: boolean;
  allTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  isFilterPanelOpen: boolean;
  onFilterPanelOpenChange: (open: boolean) => void;
  pendingFilter: DemographicsFilter;
  onPendingFilterChange: (f: DemographicsFilter) => void;
  activeFilter: DemographicsFilter;
  onFilterApply: () => void;
  onFilterReset: () => void;
  availableCountries: string[];
  availableCities: string[];
  isFetchingDemographics: boolean;
};

export function GeneralStatsToolbar({
  sortedRows,
  tableRef,
  dateRangeOption,
  onDateRangeChange,
  comparePrev,
  onComparePrevToggle,
  isFetching,
  onRefresh,
  isLoadingTags,
  allTags,
  selectedTags,
  onTagsChange,
  isFilterPanelOpen,
  onFilterPanelOpenChange,
  pendingFilter,
  onPendingFilterChange,
  activeFilter,
  onFilterApply,
  onFilterReset,
  availableCountries,
  availableCities,
  isFetchingDemographics,
}: GeneralStatsToolbarProps): JSX.Element {
  const [sheetsCopied, setSheetsCopied] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  async function handleExportSheets(): Promise<void> {
    const rows = sortedRows.filter(r => !r.isLoading && !r.error);
    if (rows.length === 0) {
      toast.warning('Нет данных для экспорта');
      return;
    }
    try {
      await copyAccountStatsTsv(rows);
      setSheetsCopied(true);
      timeoutsRef.current.push(
        setTimeout(
          () => window.open(SHEETS_URL, '_blank', 'noopener,noreferrer'),
          SHEETS_OPEN_DELAY_MS
        )
      );
      timeoutsRef.current.push(setTimeout(() => setSheetsCopied(false), SHEETS_HINT_DURATION_MS));
    } catch {
      toast.error('Не удалось скопировать данные в буфер обмена');
    }
  }

  async function handleExportPdf(): Promise<void> {
    if (!tableRef.current) return;
    const rows = sortedRows.filter(r => !r.isLoading && !r.error);
    if (rows.length === 0) {
      toast.warning('Нет данных для экспорта');
      return;
    }
    try {
      await exportAccountStatsPdf(tableRef.current);
    } catch {
      toast.error('Не удалось создать PDF');
    }
  }

  function toggleTag(tag: string): void {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  }

  return (
    <div className="py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h3 className="text-base font-semibold">Статистика аккаунтов</h3>

      <div className="flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Теги */}
          {isLoadingTags ? (
            <Skeleton className="h-8 w-32 rounded-lg" />
          ) : (
            allTags.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 h-8">
                    {selectedTags.length === 0
                      ? 'Теги'
                      : `Теги: ${selectedTags.length}`}
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {allTags.map(tag => (
                    <DropdownMenuItem
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className="flex items-center gap-2"
                    >
                      <span
                        className={`size-3.5 rounded-sm border flex-shrink-0 flex items-center justify-center ${
                          selectedTags.includes(tag) ? 'bg-primary border-primary' : 'border-muted-foreground'
                        }`}
                      >
                        {selectedTags.includes(tag) && (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="none"
                            className="text-white"
                          >
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      {tag}
                    </DropdownMenuItem>
                  ))}
                  {selectedTags.length > 0 && (
                    <>
                      <div className="border-t my-1" />
                      <DropdownMenuItem onClick={() => onTagsChange([])}>
                        Сбросить фильтр
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )
          )}

          {/* Демографический фильтр */}
          <DemographicsFilterPanel
            open={isFilterPanelOpen}
            onOpenChange={onFilterPanelOpenChange}
            pendingFilter={pendingFilter}
            onChange={onPendingFilterChange}
            onApply={onFilterApply}
            onReset={onFilterReset}
            activeFilter={activeFilter}
            availableCountries={availableCountries}
            availableCities={availableCities}
            isFetchingDemographics={isFetchingDemographics}
            disabled={!isExactDemographicsMatch(dateRangeOption)}
          />

          <div className="h-5 w-px bg-border" />

          {/* Динамика */}
          <Button
            variant={comparePrev ? 'default' : 'outline'}
            size="sm"
            className="gap-1.5"
            onClick={onComparePrevToggle}
          >
            <TrendingUp className="size-4" />
            Динамика
          </Button>

          {/* Дата + обновить */}
          <div className="flex items-center gap-1">
            <DateRangeSelector value={dateRangeOption} onChange={onDateRangeChange} />
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onRefresh}
              title="Обновить"
              disabled={isFetching}
            >
              {isFetching ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
            </Button>
          </div>

          {/* Экспорт */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <FileDown className="size-4" />
                Экспорт
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPdf}>
                <FileDown className="size-4" />
                Скачать PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportSheets} disabled={sheetsCopied}>
                <TableProperties className="size-4" />
                Google Таблицы
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {sheetsCopied && <SheetsClipboardHint />}
      </div>
    </div>
  );
}
